/**
 * ScanSetupGuide — persistent "How to scan" help for Live Scan.
 *
 * Three pages, switched by a segmented control:
 *
 *   1. "Pokédex" (default from the Dex) — the streaming grid scan: park the
 *      Nexus Dex HUD TOP-RIGHT over the Pokédex header, keep the selected
 *      filter pill + full 4-wide grid visible, scroll slowly and hold. Also
 *      teaches the HUD's number-chip language: light chip = newly registered,
 *      dark green chip = already registered (the "why isn't it picking this
 *      up" confusion is usually a re-scan of an already-caught entry),
 *      orange-red = seen not caught, red = not caught (2026-09-24).
 *
 *   2. "Pokémon" (default from My Pokémon) — the individual-Pokémon scan:
 *      open one of your own Pokémon in GO and leave the HUD where it is: on a
 *      Pokémon page it turns into a narrow column top-right (2026-09-08) and
 *      clears the CP and the IV bars by itself. (Before the portrait HUD the
 *      lesson was to park the landscape card bottom-right; near the top it sat
 *      over the CP and the scan stops reading it, so the copy shows the spot
 *      rather than offering one. Then open the appraisal and flip through.
 *
 *   3. "Detail" (Pro) — the single-Pokémon DETAIL-page screenshot, which is
 *      UPLOAD-ONLY: one screenshot of a Pokémon's page updates every dex it
 *      has at once.
 *
 * Copy note (2026-08-31, owner direction): the floating window is called the
 * "Nexus Dex HUD" everywhere a user can read it. "PiP" stays in code names and
 * bridge APIs only.
 *
 * Pages 1 and 2 show real captures of the HUD in place
 * (client/public/tutorial/scan-hud-*.jpg, downscaled to ~100KB each); page 3
 * is still an app-drawn SVG. Informational only — this sheet never touches
 * OCR, the broadcast bridge, HUD control, or any recognition logic.
 */
import { useEffect, useState, type ReactNode } from "react";
import { X, ArrowDown, Crown, ArrowRight } from "lucide-react";
import { hashNavigate } from "@/lib/hashRouter";
import {
  CHIP_SEEN_BG,
  CHIP_SEEN_TEXT,
  ScanHudPreview,
} from "@/components/ScanHudPreview";

export interface ScanSetupGuideProps {
  open: boolean;
  onClose: () => void;
  /** Page shown when the sheet opens; My Pokémon's menu opens on "pokemon". */
  initialPage?: GuidePage;
}

export type GuidePage = "live" | "pokemon" | "detail";

/* The HUD number-chip colours live with the picture that carries them, in
 * ScanHudPreview. The "ALREADY" pill on the Pokémon page borrows the dark
 * chip's colours so the note and the picture speak one language. */

const LIVE_STEPS: Array<{ n: number; text: string }> = [
  { n: 1, text: "In your Pokémon GO Pokédex, tap the filter you want to scan (Shiny, 100%, XXS) so the reader can fill it to the correct dex in Nexus Dex." },
  { n: 2, text: "Park the Nexus Dex HUD top-right, over the “recently registered” header. Nothing there gets scanned, so it's a great spot for it." },
  { n: 3, text: "Keep the full 4-wide grid on screen, then scroll slowly and hold for a beat so each row can land." },
  { n: 4, text: "The Nexus Dex HUD mirrors the four rows on your screen and scrolls with you. Light = just registered, dark green = already in your Nexus Dex, grey = being confirmed, orange-red = seen but not caught, red = not caught. A number with no chip has not been read yet." },
];

const POKEMON_STEPS: Array<{ n: number; text: string }> = [
  { n: 1, text: "Start a Live Scan, then open one of your Pokémon in Pokémon GO." },
  { n: 2, text: "Leave the Nexus Dex HUD where it is. On a Pokémon page it turns into a narrow column top-right, so your CP and IV bars stay in view." },
  { n: 3, text: "Open the appraisal. CP, HP and the IV bars all land in My Pokémon together." },
  { n: 4, text: "To register moves for the same Pokémon, click out of appraisal and they will be detected (it's ok to scroll down a little to capture them while CP is still in frame)." },
];

const DETAIL_STEPS: Array<{ n: number; text: string }> = [
  { n: 1, text: "In your Pokémon GO Pokédex, tap a single Pokémon to open its page (the one with the CAUGHT / INFO / BATTLE tabs)." },
  { n: 2, text: "Keep the whole card grid in view. The ones you own show in colour, and the rest sit there as faded silhouettes." },
];

export function ScanSetupGuide({ open, onClose, initialPage = "live" }: ScanSetupGuideProps) {
  const [page, setPage] = useState<GuidePage>(initialPage);
  // Re-seat on the caller's page each time the sheet opens (the My Pokémon
  // menu opens on "pokemon"; the Dex menu on "live").
  useEffect(() => {
    if (open) setPage(initialPage);
  }, [open, initialPage]);
  if (!open) return null;

  const subtitle =
    page === "live"
      ? "Set it up once and Nexus Dex reads the rows as you scroll."
      : page === "pokemon"
        ? "Open a Pokémon and its CP, IVs and moves land in My Pokémon."
        : "One screenshot of a Pokémon's page updates every dex it has.";

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end md:items-center justify-center bg-black/75 backdrop-blur-sm anim-fade-up"
      role="dialog"
      aria-modal="true"
      aria-label="How to scan"
      data-testid="scan-setup-guide"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full md:max-w-md v2-neon-card p-5 max-h-[90dvh] overflow-y-auto rounded-t-3xl md:rounded-3xl"
        style={{ background: "var(--surface-dark-7)", border: "1px solid var(--accent-fill-glow)" }}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.22em] font-bold"
              style={{ color: "var(--livescan-eyebrow)" }}
            >
              SCAN SETUP
            </p>
            <h3
              className="text-lg font-extrabold mt-1"
              style={{ color: "var(--livescan-title)" }}
              data-testid="scan-setup-guide-title"
            >
              How to scan
            </h3>
            <p className="text-[11px] mt-1" style={{ color: "var(--livescan-text-muted)" }}>
              {subtitle}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            data-testid="scan-setup-guide-close"
            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
            style={{
              background: "var(--surface-dark-9)",
              border: "1px solid var(--accent-fill-glow)",
              color: "var(--livescan-text)",
            }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Page switcher */}
        <div
          className="flex gap-1 p-1 rounded-2xl mb-4"
          style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow-subtle)" }}
          role="tablist"
          data-testid="scan-setup-guide-tabs"
        >
          <GuideTab active={page === "live"} onClick={() => setPage("live")} testid="scan-setup-guide-tab-live">
            Pokédex
          </GuideTab>
          <GuideTab active={page === "pokemon"} onClick={() => setPage("pokemon")} testid="scan-setup-guide-tab-pokemon">
            Pokémon
          </GuideTab>
          <GuideTab active={page === "detail"} onClick={() => setPage("detail")} testid="scan-setup-guide-tab-detail">
            <Crown className="w-3 h-3" /> Detail
          </GuideTab>
        </div>

        {page === "live" ? <LivePage /> : page === "pokemon" ? <PokemonPage /> : <DetailPage onClose={onClose} />}

        <button
          onClick={onClose}
          data-testid="scan-setup-guide-done"
          className="w-full mt-4 rounded-2xl py-3 text-sm font-bold"
          style={{
            background: "var(--accent-fill-glow-faint)",
            border: "1px solid var(--accent-fill-glow)",
            color: "var(--livescan-text)",
            minHeight: 48,
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

function GuideTab({
  active,
  onClick,
  testid,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testid: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={testid}
      className="flex-1 inline-flex items-center justify-center gap-1 py-2 rounded-xl text-[12px] font-bold transition-colors"
      style={{
        background: active ? "var(--accent-fill-glow-faint)" : "transparent",
        border: active ? "1px solid var(--accent-fill-glow)" : "1px solid transparent",
        color: active ? "var(--livescan-badge)" : "var(--livescan-text-muted)",
        minHeight: 40,
      }}
    >
      {children}
    </button>
  );
}

// ── Page 1: live grid scan ───────────────────────────────────────────────────

function LivePage() {
  return (
    <>
      <ScanSetupDiagram />

      <ol className="mt-4 flex flex-col gap-2.5" data-testid="scan-setup-guide-steps">
        {LIVE_STEPS.map((step) => (
          <StepRow key={step.n} n={step.n} text={step.text} />
        ))}
      </ol>

      <div
        className="text-[11px] mt-4 rounded-2xl p-3 leading-relaxed flex items-start gap-2"
        style={{
          background: "hsl(43 96% 18% / 0.35)",
          border: "1px solid hsl(43 96% 60% / 0.45)",
          color: "var(--livescan-text)",
        }}
        data-testid="scan-setup-guide-toprow-tip"
      >
        <ArrowDown className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "hsl(43 96% 70%)" }} />
        <span>
          <strong style={{ color: "hsl(43 96% 72%)" }}>Top row tip:</strong> the very
          top row likes to hide under the header. Gently pull the Pokédex down
          (overscroll) and hold for a second so we can read it before it springs back.
        </span>
      </div>
    </>
  );
}

// ── Page 2: individual-Pokémon scan (My Pokémon) ─────────────────────────────

function PokemonPage() {
  return (
    <>
      <PokemonScanDiagram />

      <ol className="mt-4 flex flex-col gap-2.5" data-testid="scan-setup-guide-pokemon-steps">
        {POKEMON_STEPS.map((step) => (
          <StepRow key={step.n} n={step.n} text={step.text} />
        ))}
      </ol>

      <div
        className="text-[11px] mt-4 rounded-2xl p-3 leading-relaxed flex items-start gap-2"
        style={{
          background: "var(--accent-fill-glow-faint)",
          border: "1px solid var(--accent-fill-glow)",
          color: "var(--livescan-text)",
        }}
        data-testid="scan-setup-guide-pokemon-note"
      >
        <span
          className="shrink-0 mt-0.5 inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-extrabold"
          style={{ background: CHIP_SEEN_BG, color: CHIP_SEEN_TEXT }}
          aria-hidden
        >
          ALREADY
        </span>
        <span>
          Scanned it before? The Nexus Dex HUD says{" "}
          <strong style={{ color: "var(--livescan-badge)" }}>Already in My Pokémon</strong> and
          shows you its stats instead of adding a duplicate. That's a confirmation, not a miss.
        </span>
      </div>
    </>
  );
}

// ── Page 3: single-Pokémon detail-page upload (Pro) ──────────────────────────

function DetailPage({ onClose }: { onClose: () => void }) {
  return (
    <>
      <h4
        className="text-sm font-extrabold mb-2 inline-flex items-center gap-1.5"
        style={{ color: "var(--livescan-title)" }}
        data-testid="scan-setup-guide-detail-heading"
      >
        <Crown className="w-3.5 h-3.5" style={{ color: "var(--livescan-badge)" }} />
        Pokémon Detail
      </h4>

      <DetailScanDiagram />

      {/* Owner direction (2026-08-31): the Pro block leads the section under
          the graphic, so the paywall is clear before the steps. */}
      <div
        className="text-[11px] mt-3 rounded-2xl p-3 leading-relaxed flex items-start gap-2"
        style={{
          background: "var(--accent-fill-glow-faint)",
          border: "1px solid var(--accent-fill-glow)",
          color: "var(--livescan-text)",
        }}
        data-testid="scan-setup-guide-detail-note"
      >
        <Crown className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "var(--livescan-badge)" }} />
        <span>
          <strong style={{ color: "var(--livescan-badge)" }}>Pro feature.</strong> A lovely
          way to fill in one Pokémon fast, straight from a single screenshot. Upload it
          from the same “Upload screenshots” button.
        </span>
      </div>

      <ol className="mt-3 flex flex-col gap-2.5" data-testid="scan-setup-guide-detail-steps">
        {DETAIL_STEPS.map((step) => (
          <StepRow key={step.n} n={step.n} text={step.text} />
        ))}
        <StepRow n={3}>
          Upload it here and every dex for that Pokémon updates at the same time: shiny,
          lucky, XXL, XXS and the rest, all from the one page.
          <button
            type="button"
            onClick={() => {
              onClose();
              hashNavigate("/settings/billing");
            }}
            data-testid="scan-setup-guide-detail-pro-link"
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1"
            style={{
              background: "var(--accent-fill-glow-faint)",
              border: "1px solid var(--accent-fill-glow)",
              color: "var(--livescan-badge)",
            }}
          >
            <Crown className="w-3 h-3" />
            See Nexus Dex Pro
            <ArrowRight className="w-3 h-3" />
          </button>
        </StepRow>
      </ol>
    </>
  );
}

/**
 * The two live-scan pictures come from ScanHudPreview, shared with the scan
 * menu's own preview (2026-08-31): ONE component, so a legend can never go
 * stale on one surface and not the other. These wrappers keep the guide's card
 * framing and its testids.
 */
function ScanSetupDiagram() {
  return (
    <div
      className="rounded-2xl p-3"
      style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow-subtle)" }}
    >
      <ScanHudPreview
        kind="live"
        testId="scan-setup-guide-diagram"
        legendTestId="scan-setup-guide-legend"
      />
    </div>
  );
}

function PokemonScanDiagram() {
  return (
    <div
      className="rounded-2xl p-3"
      style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow-subtle)" }}
    >
      <ScanHudPreview
        kind="pokemon"
        testId="scan-setup-guide-pokemon-diagram"
        legendTestId="scan-setup-guide-pokemon-legend"
      />
    </div>
  );
}

/**
 * App-drawn diagram for the single-Pokémon detail-page scan. A stylized phone
 * showing the real Pokémon GO detail page: the sprite + "0025 NAME" title (we
 * read the number + name), the form tiles under the name, the CAUGHT/INFO/BATTLE
 * tabs, and the variant card grid — owned variants drawn in colour, unowned ones
 * as a faded silhouette. No screenshot is committed.
 */
function DetailScanDiagram() {
  // Variant grid: 4 wide × 2 rows. `owned` = colour sprite, else silhouette.
  const labels = [
    ["", "SHINY", "100%", "LUCKY"],
    ["XXL", "XXS", "SHADOW", "MEGA"],
  ];
  const owned = [
    [true, true, false, true],
    [true, false, true, true],
  ];
  const gx = 30;
  const gy = 214;
  const cw = 52;
  const ch = 54;
  const gap = 6;
  const cells: ReactNode[] = [];
  labels.forEach((row, r) => {
    row.forEach((label, c) => {
      const x = gx + c * (cw + gap);
      const y = gy + r * (ch + gap);
      const isOwned = owned[r][c];
      const selected = r === 0 && c === 0;
      cells.push(
        <g key={`${r}-${c}`}>
          <rect
            x={x}
            y={y}
            width={cw}
            height={ch}
            rx={6}
            fill="var(--accent-fill-glow-faint)"
            stroke={selected ? "var(--accent-fill-glow)" : "var(--accent-fill-glow-subtle)"}
            strokeWidth={selected ? 2 : 1}
          />
          {/* sprite: colour dot if owned, faded silhouette if not */}
          <circle
            cx={x + cw / 2}
            cy={y + 20}
            r={11}
            fill={isOwned ? "var(--accent-fill-glow-strong)" : "hsl(214 40% 60% / 0.35)"}
          />
          {/* variant label */}
          <text
            x={x + cw / 2}
            y={y + ch - 7}
            fontSize={7.5}
            fontWeight="700"
            textAnchor="middle"
            fill={isOwned ? "var(--livescan-badge)" : "var(--livescan-text-muted)"}
          >
            {label || "CAUGHT"}
          </text>
        </g>,
      );
    });
  });

  return (
    <div
      className="rounded-2xl p-3"
      style={{ background: "var(--surface-dark-9)", border: "1px solid var(--accent-fill-glow-subtle)" }}
      data-testid="scan-setup-guide-detail-diagram"
    >
      <svg
        viewBox="0 0 290 470"
        width="100%"
        role="img"
        aria-label="Diagram: a single Pokémon's detail page. Its dex number and name at the top, the form tiles under the name, and the grid of variant cards where owned variants are in colour and unowned ones are faded silhouettes."
        style={{ maxHeight: 340, display: "block", margin: "0 auto" }}
      >
        {/* Phone frame */}
        <rect x={10} y={10} width={270} height={450} rx={28} fill="var(--surface-dark-7)" stroke="var(--accent-fill-glow)" strokeWidth={2} />

        {/* Sprite */}
        <circle cx={145} cy={44} r={22} fill="var(--accent-fill-glow-strong)" />

        {/* Title: dex number + name (the read key) */}
        <text x={145} y={92} fontSize={15} fontWeight="800" textAnchor="middle" fill="var(--livescan-title)">
          0025 PIKACHU
        </text>
        <rect x={44} y={78} width={202} height={20} rx={6} fill="none" stroke="var(--accent-fill-glow)" strokeWidth={1.5} strokeDasharray="5 3" />
        <text x={145} y={112} fontSize={8.5} fontWeight="700" textAnchor="middle" fill="var(--livescan-badge)">
          ↑ number + name read here
        </text>

        {/* Form tiles under the name (base + regional) */}
        <rect x={104} y={122} width={38} height={38} rx={8} fill="var(--accent-fill-glow-faint)" stroke="var(--accent-fill-glow)" strokeWidth={1.5} />
        <circle cx={123} cy={139} r={10} fill="var(--accent-fill-glow-strong)" />
        <rect x={148} y={122} width={38} height={38} rx={8} fill="var(--accent-fill-glow-faint)" stroke="var(--accent-fill-glow-subtle)" strokeWidth={1} />
        <circle cx={167} cy={139} r={10} fill="hsl(214 40% 60% / 0.35)" />
        <text x={167} y={172} fontSize={7.5} fontWeight="700" textAnchor="middle" fill="var(--livescan-text-muted)">ALOLAN</text>
        <text x={214} y={144} fontSize={8} fontWeight="700" fill="var(--livescan-badge)">forms</text>

        {/* CAUGHT / INFO / BATTLE tab row */}
        <text x={52} y={196} fontSize={8.5} fontWeight="800" fill="var(--livescan-title)">CAUGHT</text>
        <text x={130} y={196} fontSize={8.5} fontWeight="700" fill="var(--livescan-text-muted)">INFO</text>
        <text x={196} y={196} fontSize={8.5} fontWeight="700" fill="var(--livescan-text-muted)">BATTLE</text>

        {/* Variant card grid */}
        {cells}
      </svg>

      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1" data-testid="scan-setup-guide-detail-legend">
        <LegendDot color="var(--accent-fill-glow-strong)" label="Owned (colour), recorded" />
        <LegendDot color="hsl(214 40% 60% / 0.5)" label="Silhouette, not caught" />
      </div>
    </div>
  );
}

function StepRow({
  n,
  text,
  children,
}: {
  n: number;
  text?: string;
  children?: ReactNode;
}) {
  return (
    <li className="flex items-start gap-2.5" data-testid="scan-setup-guide-step">
      <span
        className="shrink-0 mt-0.5 w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] font-extrabold tabular-nums"
        style={{
          background: "var(--accent-fill-glow-faint)",
          border: "1px solid var(--accent-fill-glow)",
          color: "var(--livescan-badge)",
        }}
      >
        {n}
      </span>
      <span className="text-[12px] leading-relaxed" style={{ color: "var(--livescan-text)" }}>
        {children ?? text}
      </span>
    </li>
  );
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px]" style={{ color: "var(--livescan-text-muted)" }}>
      <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: color }} />
      {label}
    </span>
  );
}

